import { computed, ref } from "vue";
import { db } from "@src/services/db";
import { useChatSessionStore } from "@src/stores/chatSession";
import { toggleSessionTagRequest, type Tag } from "@src/services/tags";
import {Toast} from "@src/plugins/toast";

function applyToggle(current: Tag[], tag: Tag): Tag[] {
    const exists = current.some(t => t.id === tag.id);
    return exists ? current.filter(t => t.id !== tag.id) : [...current, tag];
}

async function safeUpdateSessionInDB(sessionId: number, tags: Tag[]) {
    try {
        await db.sessions.update(sessionId, { tags, updated_at: Date.now() } as any);
    } catch (e) {
        console.warn("⚠️ indexedDB update falhou", e);
    }
}

export function useSessionTags() {

    const sessionStore = useChatSessionStore();

    const sessionId = computed(() => sessionStore.selectedSession?.id ?? null);
    const selectedTags = computed<Tag[]>(() => sessionStore.selectedSession?.tags ?? []);

    // ✅ fila por sessão: uma request por vez
    const queue = ref<Promise<void>>(Promise.resolve());

    // versão monotônica: cada clique incrementa
    const version = ref(0);

    // opcional: trava por tag (evita duplo clique no mesmo item)
    const pendingTagIds = ref<Set<number>>(new Set());
    function isPending(tagId: number) {
        return pendingTagIds.value.has(tagId);
    }

    function setLocalTags(tags: Tag[]) {
        if (sessionStore.selectedSession) {
            sessionStore.selectedSession.tags = tags;
        }
        void safeUpdateSessionInDB(Number(sessionId.value), tags);
    }

    async function toggleTag(tag: Tag) {
        const sid = sessionId.value;
        if (!sid) return;

        const tagId = Number(tag.id);
        if (pendingTagIds.value.has(tagId)) return;

        pendingTagIds.value.add(tagId);

        // ✅ 1) optimistic instantâneo
        const prev = [...selectedTags.value];
        const optimistic = applyToggle(prev, tag);
        setLocalTags(optimistic);

        // marca a versão desse clique
        const myVersion = ++version.value;

        // ✅ 2) enfileira a request (serializado)
        queue.value = queue.value
            .then(async () => {
                // faz request
                const res = await toggleSessionTagRequest(Number(sid), tagId);

                // se já teve clique depois, não deixa essa resposta “mandar” no UI
                if (myVersion !== version.value) return;

                if (!res?.success) {
                    // rollback (apenas se ainda for a versão atual)
                    setLocalTags(prev);
                    return;
                }

                // aplica fonte de verdade (apenas se ainda for a versão atual)
                setLocalTags(res.tags);
            })
            .catch((e) => {
                // erro de rede: rollback se ainda for a versão atual
                if (myVersion === version.value) {
                    setLocalTags(prev);
                }

                Toast.fire(
                    e.response?.data?.message || "Não foi possível atualizar a tag.",
                    "",
                    "error"
                );

                console.error("❌ toggleTag request falhou", e);
            })
            .finally(() => {
                pendingTagIds.value.delete(tagId);
            });

        // não precisa await aqui; UI já atualizou
    }

    return { sessionId, selectedTags, toggleTag, isPending };
}

